import { Config } from '../types/Config';
import { Run } from '../types/Run';
import { SessionStatistics, AggregateStatistics, CalculationMethod } from '../types/Statistics';
import { isSentinelRunTs } from './sessionTime';

export const CDA_KEY = 'CdAN_xG3';

/**
 * Calculate mean of an array of numbers
 */
export function calculateMean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, val) => sum + val, 0) / values.length;
}

/**
 * Calculate population standard deviation
 */
export function calculateStdDev(values: number[]): number {
  if (values.length === 0) return 0;
  const mean = calculateMean(values);
  const squaredDiffs = values.map((val) => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / values.length;
  return Math.sqrt(variance);
}

/**
 * Calculate Coefficient of Variation (CV) as percentage
 * CV = (StdDev / Mean) * 100
 */
export function calculateCV(mean: number, stdDev: number): number {
  if (mean === 0) return 0;
  return (stdDev / mean) * 100;
}

/**
 * Calculate Typical Error at 68% CI
 * TE = StdDev / sqrt(2)
 */
export function calculateTE(stdDev: number): number {
  return stdDev / Math.sqrt(2);
}

/**
 * Calculate lap CdA values from traverse values
 * Each lap is the average of 2 consecutive traverses
 * If odd number of traverses, last one is its own lap
 */
export function calculateLapCdAValues(traverseValues: number[]): number[] {
  const laps: number[] = [];

  for (let i = 0; i < traverseValues.length; i += 2) {
    if (i + 1 < traverseValues.length) {
      // Average of two traverses
      laps.push((traverseValues[i] + traverseValues[i + 1]) / 2);
    } else {
      // Odd traverse at end - counts as its own lap
      laps.push(traverseValues[i]);
    }
  }

  return laps;
}

/**
 * Extract all CdAN_xG3 traverse values from Config A runs
 */
export function extractConfigACdAValues(configs: Config[]): number[] {
  // Find Config A (letter === 'A' or letter_number === 0)
  const configA = configs.find((c) => c.letter === 'A' || (c as any).letter_number === 0);

  if (!configA || !configA.runs) return [];

  const allTraverseValues: number[] = [];

  for (const run of configA.runs) {
    const cdaValues = run.traverse?.[CDA_KEY];
    if (Array.isArray(cdaValues)) {
      allTraverseValues.push(...cdaValues.filter((v) => v != null && !isNaN(v)));
    }
  }

  return allTraverseValues;
}

/**
 * Get total run count for Config A
 */
export function getConfigARunCount(configs: Config[]): number {
  const configA = configs.find((c) => c.letter === 'A' || (c as any).letter_number === 0);
  return configA?.runs?.length || 0;
}

/**
 * Calculate time span between first and last run in Config A
 * Returns format "HH:MM"
 */
export function calculateTimeSpan(configs: Config[]): string {
  const configA = configs.find((c) => c.letter === 'A' || (c as any).letter_number === 0);
  if (!configA || !configA.runs || configA.runs.length === 0) return '00:00';

  // Un-stamped runs carry the year-9999 creation sentinel; including one turns
  // this span into ~70 million hours.
  const timestamps = configA.runs
    .filter((run: Run) => !isSentinelRunTs(run.run_ts))
    .map((run: Run) => new Date(run.run_ts).getTime())
    .filter((ts) => !isNaN(ts))
    .sort((a, b) => a - b);

  if (timestamps.length < 2) return '00:00';

  const diffMs = timestamps[timestamps.length - 1] - timestamps[0];
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Calculate differences from mean for lap values
 * Each difference = lap value - session mean
 */
export function calculateMeanDifferences(lapValues: number[]): number[] {
  if (lapValues.length === 0) return [];
  const mean = calculateMean(lapValues);
  return lapValues.map((val) => val - mean);
}

/**
 * Calculate SD using sequential differences method
 * SD = StdDev of consecutive differences (d1 = Lap2 - Lap1, etc.)
 */
export function calculateSequentialStdDev(lapValues: number[]): number {
  const differences = calculateLapDifferences(lapValues);
  if (differences.length === 0) return 0;
  return calculateStdDev(differences);
}

/**
 * Calculate sample standard deviation (using n-1 denominator)
 * Used for per-session analysis as a population estimate
 */
export function calculateSampleStdDev(values: number[]): number {
  if (values.length <= 1) return 0;
  const mean = calculateMean(values);
  const squaredDiffs = values.map((val) => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

/**
 * Calculate SD using mean differences method
 * SD = Sample StdDev (n-1) of lap values for population estimate
 */
export function calculateMeanBasedStdDev(lapValues: number[]): number {
  if (lapValues.length === 0) return 0;
  // Use sample standard deviation (n-1) for population estimate
  return calculateSampleStdDev(lapValues);
}

/**
 * Calculate all statistics for a session's Config A
 * @param session - The session data
 * @param riderName - The rider's name
 * @param method - 'sequential' for consecutive differences, 'mean' for differences from mean
 */
export function calculateSessionStatistics(
  session: any,
  riderName: string,
  method: CalculationMethod = 'sequential'
): SessionStatistics | null {
  if (!session?.configs) return null;

  const traverseValues = extractConfigACdAValues(session.configs);
  if (traverseValues.length === 0) return null;

  const lapValues = calculateLapCdAValues(traverseValues);
  const mean = calculateMean(lapValues);

  // Calculate StdDev based on selected method
  const stdDev =
    method === 'sequential' ? calculateSequentialStdDev(lapValues) : calculateMeanBasedStdDev(lapValues);

  return {
    sessionId: session.id,
    date: session.date || '',
    rider: riderName,
    description: session.description || '',
    totalRuns: getConfigARunCount(session.configs),
    totalTraverses: traverseValues.length,
    totalLaps: lapValues.length,
    meanCdA: mean,
    stdDev: stdDev,
    cv: calculateCV(mean, stdDev),
    te: calculateTE(stdDev),
    timeSpan: calculateTimeSpan(session.configs),
  };
}

/**
 * Calculate median of an array of numbers
 */
export function calculateMedian(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Calculate consecutive differences between lap values
 * d1 = Lap2 - Lap1, d2 = Lap3 - Lap2, etc.
 */
export function calculateLapDifferences(lapValues: number[]): number[] {
  if (lapValues.length < 2) return [];
  const differences: number[] = [];
  for (let i = 1; i < lapValues.length; i++) {
    differences.push(lapValues[i] - lapValues[i - 1]);
  }
  return differences;
}

/**
 * Calculate pooled statistics from all session lap values
 * Uses the specified calculation method consistently
 *
 * Sequential method: Pool all consecutive differences across sessions
 * Mean method: Pool all mean-differences across sessions (each session's differences from its own mean)
 *
 * Pooled SD = SD of all pooled differences (with degrees of freedom correction for mean method)
 * Pooled TE = Pooled SD / sqrt(2)
 * Pooled CV = (Pooled SD / Overall Mean CdA) * 100
 */
export function calculatePooledStatistics(
  allLapValuesBySession: number[][],
  sessionStats: SessionStatistics[],
  method: CalculationMethod = 'sequential'
): { pooledStdDev: number; pooledCV: number; pooledTE: number } {
  // Collect all differences from all sessions based on method
  const allDifferences: number[] = [];
  let totalLapCount = 0;
  let sessionCount = 0;

  for (const lapValues of allLapValuesBySession) {
    if (lapValues.length === 0) continue;

    sessionCount++;
    totalLapCount += lapValues.length;

    if (method === 'sequential') {
      // Sequential: consecutive differences (d1 = Lap2 - Lap1, etc.)
      const differences = calculateLapDifferences(lapValues);
      allDifferences.push(...differences);
    } else {
      // Mean: differences from session mean
      const differences = calculateMeanDifferences(lapValues);
      allDifferences.push(...differences);
    }
  }

  if (allDifferences.length === 0) {
    return { pooledStdDev: 0, pooledCV: 0, pooledTE: 0 };
  }

  // Calculate pooled SD of all differences
  let pooledStdDev = calculateStdDev(allDifferences);

  // Apply degrees of freedom correction for mean method
  // When using mean differences, we estimate k separate means (one per session)
  // Correct SD: SD_correct = SD_population * sqrt((n-1)/(n-k))
  // where n = total observations, k = number of sessions (means estimated)
  if (method === 'mean' && totalLapCount > sessionCount) {
    const n = totalLapCount;
    const k = sessionCount;
    const dfCorrection = Math.sqrt((n - 1) / (n - k));
    pooledStdDev = pooledStdDev * dfCorrection;
  }

  // Calculate pooled TE = SD_diff / sqrt(2)
  const pooledTE = pooledStdDev / Math.sqrt(2);

  // Calculate overall mean CdA for CV calculation (weighted by lap count)
  const totalLaps = sessionStats.reduce((sum, s) => sum + s.totalLaps, 0);
  const weightedMeanCdA =
    totalLaps > 0 ? sessionStats.reduce((sum, s) => sum + s.meanCdA * s.totalLaps, 0) / totalLaps : 0;

  // Calculate pooled CV = (pooled SD / overall mean) * 100
  const pooledCV = weightedMeanCdA > 0 ? (pooledStdDev / weightedMeanCdA) * 100 : 0;

  return { pooledStdDev, pooledCV, pooledTE };
}

/**
 * Calculate aggregate statistics across multiple sessions
 * Uses pooled difference method for SD, CV, and TE
 * @param sessionStats - Array of per-session statistics
 * @param allLapValuesBySession - Array of lap value arrays, one per session
 * @param method - 'sequential' for consecutive differences, 'mean' for differences from mean
 */
export function calculateAggregateStatistics(
  sessionStats: SessionStatistics[],
  allLapValuesBySession?: number[][],
  method: CalculationMethod = 'sequential'
): AggregateStatistics {
  if (sessionStats.length === 0) {
    return {
      totalRiders: 0,
      totalSessions: 0,
      totalRuns: 0,
      totalLaps: 0,
      meanCdA: 0,
      pooledStdDev: 0,
      pooledCV: 0,
      pooledTE: 0,
    };
  }

  // Count unique riders
  const uniqueRiders = new Set(sessionStats.map((s) => s.rider));
  const totalRiders = uniqueRiders.size;

  const totalRuns = sessionStats.reduce((sum, s) => sum + s.totalRuns, 0);
  const totalLaps = sessionStats.reduce((sum, s) => sum + s.totalLaps, 0);

  // Calculate weighted mean CdA (weighted by lap count)
  const meanCdA =
    totalLaps > 0 ? sessionStats.reduce((sum, s) => sum + s.meanCdA * s.totalLaps, 0) / totalLaps : 0;

  // Calculate pooled statistics if lap values are provided
  let pooledStdDev = 0;
  let pooledCV = 0;
  let pooledTE = 0;

  if (allLapValuesBySession && allLapValuesBySession.length > 0) {
    const pooled = calculatePooledStatistics(allLapValuesBySession, sessionStats, method);
    pooledStdDev = pooled.pooledStdDev;
    pooledCV = pooled.pooledCV;
    pooledTE = pooled.pooledTE;
  }

  return {
    totalRiders,
    totalSessions: sessionStats.length,
    totalRuns,
    totalLaps,
    meanCdA,
    pooledStdDev,
    pooledCV,
    pooledTE,
  };
}
